import React from "react"
import { Subscription } from "rxjs"
import { Sort, Filter } from "../../api/GridRequest"
import { APIError } from "../../api/ObservableFromFetch"
import { toast } from "react-toastify"
import { Container, Row, Col, Table, Button, Spinner } from "react-bootstrap"
import { FaSync } from "react-icons/fa"
import ColumnHeaderFilter from "../ColumnHeaderFilter"
import TablePagination from "../TablePagination"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import SendingFacilityRecord from "../../api/Administration/Model/SendingFacilityRecord"
import SendingFacilityObservable, { ColumnFilters, SendingFacilityGridResponse, TimezoneObservable } from "../../api/Administration/SendingFacilities"
import { CustomizableFilterColumn } from "../ColumnsCustomizeModal/CustomizableColumn"
import SendingFacilityModal from "./SendingFacilityModal"
import Timezone from "../../api/Administration/Model/Timezone"

type Column = CustomizableFilterColumn<keyof SendingFacilityRecord, keyof ColumnFilters>

interface SendingFacilityProps {

}

interface SendingFacilityState {
  loading: boolean
  data?: SendingFacilityGridResponse
  timezones: Timezone[]
  columns: Column[]
  sort?: Sort
  filters: Filter[]
  pageNumber: number
  sendingFacilityDetails: boolean
  details?: SendingFacilityRecord
  addNew: boolean
}

const defaultColumns: Column[] = [
  { name: "SendingFacility", filter: "SendingFacility", label: "Sending Facility" },
  { name: "Namespace", filter: "Namespace", label: "Namespace" },
  { name: "SendingFacility_Code", filter: "SendingFacility_Code", label: "SendingFacility_Code" },
  { name: "WS_Url", filter: "WS_Url", label: "WS_Url" },
]

const PageSize = 20

export default class SendingFacilityPage extends React.PureComponent<SendingFacilityProps, SendingFacilityState> {
  commonSubscription?: Subscription
  subscription?: Subscription

  state: SendingFacilityState = {
    loading: false,
    data: undefined,
    timezones: [],
    details: undefined,
    columns: defaultColumns,
    sort: undefined,
    filters: [],
    pageNumber: 1,
    sendingFacilityDetails: false,
    addNew: false
  }

  componentDidMount() {
    this.commonSubscription = TimezoneObservable().subscribe(
      data  => this.setState({ timezones: data }),
      error => this.handleAPIError(error, { })
    )
    this.refreshGrid()
  }

  componentWillUnmount() {
    this.subscription && this.subscription.unsubscribe()
    this.commonSubscription && this.commonSubscription.unsubscribe()
  }

  componentDidUpdate(prevProps: SendingFacilityProps, prevState: SendingFacilityState) {
    if (this.state.sort !== prevState.sort ||
      this.state.filters !== prevState.filters ||
      this.state.pageNumber !== prevState.pageNumber) {
      this.refreshGrid()
    }
  }

  refreshGrid() {
    this.setState({ loading: true })
    this.subscription && this.subscription.unsubscribe()
    this.subscription = SendingFacilityObservable(this.state.pageNumber, PageSize, this.state.filters, this.state.sort).subscribe(
      (data: SendingFacilityGridResponse) => this.setState({ data: data, loading: false }),
      error => this.handleAPIError(error, { loading: false })
    )
  }

  pageChanged = (pageNumber: number) => this.setState({ pageNumber: pageNumber })
  sortGrid = (column: string, order?: string) => this.setState({ sort: { column: column, order: order || "" }, pageNumber: 1 })

  filterGrid(column: string, items: string[]) {
    this.setState(prevState => {
      const filters = [...prevState.filters]
      const filter = { column: column, values: items }
      let index = filters.findIndex(item => item.column === filter.column)
      if (index >= 0) {
        filters[index] = filter
      } else {
        filters.push(filter)
      }
      return { filters: filters, pageNumber: 1 }
    })
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  onSave = () => {
    this.setState({addNew: false, sendingFacilityDetails: false, details: undefined, filters: []})
  }

  showSendingFacilityDetailsModal = (data: SendingFacilityRecord) => this.setState({ sendingFacilityDetails: true, details: data })

  renderCell(row: SendingFacilityRecord, colName: keyof SendingFacilityRecord, key: number) {
    switch (colName) {
      case "SendingFacility":
        return <td key={key}><Button variant="underline-link" onClick={() => this.showSendingFacilityDetailsModal(row)}>{row[colName]}</Button></td>
      default:
        return <td key={key} className="align-middle">{row[colName]}</td>
    }
  }

  render() {
    const headerColumns = this.state.columns.map((col: Column, i) => {
      return (
        <td key={i}>
          {this.state.data && col.filter && col.filter in this.state.data && <ColumnHeaderFilter title={col.label || col.name}
            filtered={this.state.filters.findIndex(item => item.column === col.filter) >= 0}
            sort={this.state.sort && this.state.sort.column === col.name ? this.state.sort.order === "asc" ? "asc" : "desc" : undefined}
            items={this.state.data[col.filter as keyof ColumnFilters] || []}
            onSort={(order?: string) => this.sortGrid(col.name, order)}
            onFilter={(items: string[]) => col.filter && this.filterGrid(col.filter, items)}
            offsetTop={19}
          />}
          {(!this.state.data || !col.filter) && (col.label || col.name)}
        </td>
      )
    })

    const dataRows = (data: SendingFacilityGridResponse) => data.Records.map((row: SendingFacilityRecord, i: number) => <tr key={i}>{dataColumns(row)}</tr>)
    const dataColumns = (row: SendingFacilityRecord) => this.state.columns.map((col, i) => this.renderCell(row, col.name as keyof SendingFacilityRecord, i))

    const { loading, data, sendingFacilityDetails, pageNumber, addNew, timezones } = this.state

    const emptyRow = <tr><td colSpan={this.state.columns.length}>NO DATA FOUND</td></tr>
    return (
      <AppPageWrapper parentClassName={"align-items-center"} showNav={true}>
        <Container fluid className="grid-container">
          <Row className="pb-2">
            <Col lg={12} className={"text-right"}>
              <Button variant="icon-link" size="sm" onClick={() => this.setState({ filters: [] })} className="text-dark">CLEAR FILTERS<FaSync /></Button>
              <Button variant="primary" size="sm" className="mr-1" onClick={() => this.setState({ addNew: true })}>Create New</Button>
              {addNew && <SendingFacilityModal addNew onHide={() => this.setState({ addNew: false })} timezones={timezones} onSave={() => this.onSave()}/>}
              {sendingFacilityDetails && <SendingFacilityModal onHide={() => this.setState({ sendingFacilityDetails: false, details: undefined })} timezones={timezones} details={this.state.details} onSave={() => this.onSave()} />}
            </Col>
          </Row>
          <Row>
            <Col>
              {loading ? <tr><td colSpan={this.state.columns.length}><Spinner animation="border" variant="secondary" /></td></tr> :
              <Table bordered responsive>
                <thead>
                  <tr>{headerColumns}</tr>
                </thead>
                <tbody>
                  { data?.Records && data.Records.length > 0 ? dataRows(data) : emptyRow }
                </tbody>
              </Table>}
              {data && !loading &&
                <span className="total-records">Total records: {data.Total}</span>}
              {data && !loading &&
                <TablePagination pageNumber={pageNumber} pages={data.Pages} onChange={this.pageChanged} />}
            </Col>
          </Row>
        </Container>
      </AppPageWrapper>
    )
  }
}
